import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, RotateCcw, ShieldCheck } from 'lucide-react';
import { findProduct } from '../../content/products';
import { mediaFor } from '../../content/chooserMedia';
import { STEPS, stepOrder, isDone, recommend, present, whyFor, describe, optionsFor, keyOf } from '../../lib/lockChooser';
import { PICTOS_BY_STEP } from './LockPictos';
import { track } from '../../lib/shopApi';
import { doorQuery, saveDoor } from '../../lib/doorAnswers';
import { ProductCard } from './ShopBits';

// The door-first walk-through, one question at a time: pick an answer and its pictures make
// way for the next question's pictures (Hugh, 17 Sep 2026). A ranchslider is one tap — every
// sliding lock fits every slider — and a hinged door is three: timber or aluminium, then what
// is on the door, with the cards in the order that door usually has them. Nothing about
// shelter, swing direction or the backset (Hugh, 17 Sep: normal people found six "way too
// much"); the weather rating is a chip on the card and the size is taken from the door photos
// after payment — nobody who comes this way is asked for a backset (Hugh, 22 Sep 2026).
//
// The results decide, then offer (19 Sep research): "Our pick" with one line of reasoning, up
// to four alternates chosen for spread, two-word reason and warning chips on every card, the
// fit promise, and the rest folded behind "Show all". The answers travel to the product page
// in the link (and the session) so the install questions start filled in (Hugh, 18 Sep 2026).
// `brand` filters the picks to one brand (brand hubs); without it the picks span every brand.
const SHORT = {
  slides: { slides: 'Ranchslider', swings: 'Hinged door' },
  material: { aluminium: 'Aluminium', timber: 'Timber', unsure: 'Not sure' },
  onDoor: { mortice: 'Lever + mortice lock', multipoint: 'Lift-to-lock handle', deadbolt: 'Round deadlock', lever: 'Keyed knob / lever', nightlatch: 'Deadlatch', gripset: 'Entrance gripset', none: 'Blank door', other: 'Something else' },
};
const shortLabel = (step, v) => (SHORT[keyOf(step)] && SHORT[keyOf(step)][v]) || String(v);

export default function LockWizard({ brand = null, testId = 'lock-wizard', compact = false }) {
  const [a, setA] = useState({});
  const [entry, setEntry] = useState('all');
  const [showAll, setShowAll] = useState(false);
  const startedAt = useRef(null);
  const brandOf = (slug) => { const p = findProduct(slug); return p ? p.brand : null; };
  const order = stepOrder(a);
  const optionsOf = (k) => optionsFor(k, a, brand, brandOf);
  const current = order.find((k) => a[keyOf(k)] === undefined) || null;
  // A step with a single possible answer (a brand hub that only makes hinged locks) is
  // answered for the customer.
  useEffect(() => {
    if (current && optionsOf(current).length === 1) setA((s) => Object.assign({}, s, { [keyOf(current)]: optionsOf(current)[0][0] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  // Answering (or going back to) a step clears every answer after it.
  const keep = (s, stepKey) => {
    const before = stepOrder(s);
    const idx = before.indexOf(stepKey);
    const kept = new Set(before.slice(0, Math.max(idx, 0)).map(keyOf));
    const next = {};
    for (const key of Object.keys(s)) if (kept.has(key)) next[key] = s[key];
    return next;
  };
  const answer = (stepKey, v) => {
    if (!startedAt.current) { startedAt.current = Date.now(); track('chooser_start', { brand: brand || 'all' }); }
    track('chooser_step', { brand: brand || 'all', step: stepKey, answer: v, msSinceStart: Date.now() - startedAt.current });
    setA((s) => { const next = keep(s, stepKey); next[keyOf(stepKey)] = v; return next; });
  };
  const backTo = (stepKey) => { track('chooser_back', { brand: brand || 'all', to: stepKey }); setA((s) => keep(s, stepKey)); };
  const back = () => {
    const answered = order.filter((k) => a[keyOf(k)] !== undefined);
    if (answered.length) backTo(answered[answered.length - 1]);
  };
  const reset = () => { setA({}); setEntry('all'); setShowAll(false); };

  const done = isDone(a);
  const answersWithEntry = useMemo(() => (entry === 'all' ? a : Object.assign({}, a, { entry })), [a, entry]);
  const view = done ? present(answersWithEntry) : null;
  const recAll = done ? recommend(a) : { slugs: [], note: null };
  const allPicks = recAll.slugs.map(findProduct).filter(Boolean);
  const brandKeeps = (p) => !brand || p.brand === brand;
  const everyPick = view ? view.slugs.map(findProduct).filter(Boolean) : [];
  const noBrandMatch = brand && done && everyPick.length > 0 && !everyPick.some(brandKeeps);
  const picks = everyPick.filter(brandKeeps);
  const spread = view ? view.visible.map(findProduct).filter(Boolean).filter(brandKeeps) : [];
  const visible = spread.length ? spread : picks.slice(0, 5);
  const shown = showAll ? picks : visible;
  const pick = shown[0] || null;
  const canFilterFingerprint = allPicks.some((p) => (p.unlock || []).some((u) => /fingerprint/i.test(u)));
  const canFilterPhone = allPicks.some((p) => (p.ecosystems || []).length > 0);

  useEffect(() => {
    if (done) track('chooser_result', { brand: brand || 'all', answers: a, picks: recAll.slugs, visible: view ? view.visible : [] });
    // The finished walk is kept for the visit: the product page and the cart read it back.
    if (done) saveDoor(a);
    setShowAll(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, recAll.slugs.join(',')]);

  const answered = order.filter((k) => a[keyOf(k)] !== undefined);
  // Three steps once the first answer says the door swings; a ranchslider has no count.
  const total = a.slides === 'swings' ? order.length : null;
  const stepNo = answered.length + 1;
  const query = doorQuery(a);
  const stepHelp = current ? STEPS[current].help : null;
  // The hardware step is a list on a phone (every option on one screen) and cards from sm up.
  const isList = current === 'onDoor';
  const photosHref = `/shop/find-my-lock/photos?about=${encodeURIComponent(describe(a))}`;

  return (
    <div className={`rounded-2xl border border-gray-200 bg-gray-50 ${compact ? 'p-4 sm:p-5' : 'p-5 sm:p-8'}`} data-testid={testId}>
      {answered.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid={`${testId}-answers`}>
          {answered.map((k) => (
            <button key={k} type="button" onClick={() => backTo(k)} className="inline-flex items-center gap-1 rounded-full bg-white border border-gray-300 px-3 py-1 text-sm text-gray-800 hover:border-gray-500" title="Change this answer">
              {shortLabel(k, a[keyOf(k)])} <span className="text-gray-400">✕</span>
            </button>
          ))}
        </div>
      )}

      {!done && current && (
        <div data-testid={`${testId}-step-${current}`}>
          <p className={`font-semibold text-gray-900 ${compact ? 'text-lg' : 'text-xl sm:text-2xl'} ${stepHelp ? 'mb-1' : 'mb-4'}`}>
            <span className="text-sm font-semibold uppercase tracking-widest text-red-600 block mb-1">Step {stepNo}{total ? ` of ${total}` : ''}</span>
            {STEPS[current].q}
          </p>
          {stepHelp && <p className="text-sm text-gray-600 mb-4" data-testid={`${testId}-help`}>{stepHelp}</p>}

          <div className={isList ? 'grid gap-2 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' : `grid gap-3 sm:gap-4 ${optionsOf(current).length <= 2 ? 'grid-cols-2' : 'grid-cols-3'}`} data-testid={`${testId}-options`}>
            {optionsOf(current).map(([v, label, hint]) => {
              const media = mediaFor(current, v, a);
              const Picto = PICTOS_BY_STEP[current] && PICTOS_BY_STEP[current][v];
              const big = optionsOf(current).length <= 2 && !compact;
              let visual = null;
              if (media && big) {
                // The two big door cards show the whole square photo at full size (Hugh, 18 Sep 2026).
                visual = <img src={media.src} alt={media.alt} className="w-full aspect-square object-cover bg-gray-100" loading="eager" />;
              } else if (media) {
                // The whole photo, never zoomed in (Hugh, 24 Sep 2026): the chooser photos are
                // prepared at 4:3 and shown in a 4:3 frame, contained.
                visual = <img src={media.sm || media.src} srcSet={media.sm ? `${media.sm} 600w, ${media.src} 1200w` : undefined} sizes="(min-width: 1024px) 320px, 50vw" alt={media.alt} className={isList ? 'w-20 h-16 shrink-0 object-contain bg-white sm:w-full sm:h-auto sm:aspect-[4/3]' : 'w-full aspect-[4/3] object-contain bg-white'} loading="eager" />;
              } else if (Picto) {
                visual = (
                  <div className={isList ? 'flex items-center justify-center bg-white w-20 h-16 shrink-0 [&_svg]:w-14 [&_svg]:h-14 sm:w-full sm:h-auto sm:py-3 sm:[&_svg]:w-24 sm:[&_svg]:h-24' : `flex items-center justify-center bg-white ${big ? 'py-6 [&_svg]:w-40 [&_svg]:h-40' : compact ? 'py-2 [&_svg]:w-16 [&_svg]:h-16' : 'py-3 [&_svg]:w-20 [&_svg]:h-20 sm:[&_svg]:w-28 sm:[&_svg]:h-28'}`}><Picto /></div>
                );
              }
              return (
                <button key={String(v)} type="button" onClick={() => answer(current, v)} className={`group rounded-2xl border-2 border-gray-200 bg-white overflow-hidden text-left transition-all hover:border-blue-500 hover:shadow-lg hover:-translate-y-0.5 ${isList ? 'flex items-center gap-3 sm:block' : ''}`} data-testid={`${testId}-option-${v}`}>
                  {visual}
                  <span className={`block ${isList ? 'py-2 pr-3 sm:px-3 sm:py-3 sm:text-center' : `text-center ${big ? 'px-4 py-3' : compact ? 'px-2 py-2' : 'px-2 py-3 sm:px-3'}`}`}>
                    <span className={`block font-semibold text-gray-900 group-hover:text-blue-700 leading-snug ${big ? 'text-lg' : compact || isList ? 'text-sm sm:text-base' : 'text-sm sm:text-base'}`}>{label}</span>
                    {hint && <span className="block text-xs text-gray-500 mt-0.5 font-normal">{hint}</span>}
                  </span>
                </button>
              );
            })}
          </div>

          {answered.length > 0 && (
            <button type="button" onClick={back} className="mt-5 text-sm text-gray-600 inline-flex items-center gap-1 hover:text-gray-900">
              <ArrowLeft size={14} /> Back
            </button>
          )}
        </div>
      )}

      {brand && order.some((k) => optionsOf(k).length === 1) && (
        <p className="text-sm text-gray-600 mb-4" data-testid={`${testId}-given`}>
          {order.filter((k) => optionsOf(k).length === 1).map((k) => optionsOf(k)[0][1]).join(' · ')} — the only option {brand} makes.
        </p>
      )}

      {done && pick && (
        <div data-testid={`${testId}-recommendation`}>
          <p className="text-sm text-gray-600">{describe(a)}</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">
            Our pick: {pick.shortName}
            {shown.length > 1 && (
              <span className="text-base font-normal text-gray-600">
                {` — and ${shown.length - 1} other${shown.length > 2 ? 's' : ''} worth a look`}
                {picks.length > shown.length ? ` · ${picks.length - shown.length} more fit` : ''}
              </span>
            )}
          </p>
          <p className="text-sm text-gray-700 mt-1" data-testid={`${testId}-why`}>{whyFor(pick, answersWithEntry)}</p>
          <p className="text-sm text-gray-800 mt-2 mb-3 flex items-start gap-1.5" data-testid={`${testId}-promise`}>
            <ShieldCheck size={16} className="text-green-700 shrink-0 mt-0.5" /> <span>{view.promise}</span>
          </p>
          {(canFilterFingerprint || canFilterPhone) && (
            <div className="flex flex-wrap items-center gap-2 mb-4 text-sm" data-testid={`${testId}-entry-filter`}>
              <span className="text-gray-600">Show:</span>
              {[['all', 'Everything that fits'], canFilterFingerprint && ['fingerprint', 'Fingerprint'], canFilterPhone && ['phone', 'Apple Home / Google / Alexa']].filter(Boolean).map(([v, label]) => (
                <button key={v} type="button" onClick={() => { setEntry(v); setShowAll(false); track('chooser_filter', { brand: brand || 'all', entry: v }); }} className={`rounded-full border px-3 py-1.5 ${entry === v ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'}`} aria-pressed={entry === v}>
                  {label}
                </button>
              ))}
            </div>
          )}
          <div className={`grid gap-4 ${compact ? 'sm:grid-cols-2' : 'sm:grid-cols-2 xl:grid-cols-3'}`}>
            {shown.map((p, i) => {
              const c = (view && view.chips[p.slug]) || { reasons: [], warnings: [] };
              return (
                <ProductCard
                  key={p.slug}
                  product={p}
                  sizes={false}
                  query={query}
                  pickBadge={i === 0 ? 'Our pick' : null}
                  chips={c.reasons.map((t) => ({ text: t, tone: 'reason' })).concat(c.warnings.map((t) => ({ text: t, tone: 'warn' })))}
                  onClick={() => track('chooser_pick_click', { brand: brand || 'all', slug: p.slug, position: i + 1, answers: a })}
                />
              );
            })}
          </div>
          {picks.length > visible.length && (
            <button type="button" onClick={() => { setShowAll((v) => !v); if (!showAll) track('chooser_more', { brand: brand || 'all', total: picks.length }); }} className="mt-4 px-4 py-2 rounded-full text-sm font-semibold border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white" data-testid={`${testId}-more`}>
              {showAll ? 'Show fewer' : `Show all ${picks.length} that fit`}
            </button>
          )}
          {view.note && <p className="text-sm text-gray-700 mt-3">{view.note}</p>}
          {a.slides === 'slides' && (
            <p className="text-sm text-gray-700 mt-2" data-testid={`${testId}-photos-line`}>
              Something unusual on the door, or more than one lock? <Link to={photosHref} className="text-red-600 font-semibold">Send photos instead</Link>.
            </p>
          )}
          <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
            <button type="button" onClick={reset} className="text-gray-700 inline-flex items-center gap-1 hover:text-gray-900">
              <RotateCcw size={14} /> Start again
            </button>
          </div>
        </div>
      )}
      {done && !pick && entry !== 'all' && !noBrandMatch && (
        <p className="text-sm text-gray-700 mt-2" data-testid={`${testId}-filter-empty`}>
          Nothing that fits this door has that. <button type="button" onClick={() => setEntry('all')} className="text-red-600 font-semibold">Show everything that fits</button>
        </p>
      )}
      {noBrandMatch && (
        <p className="text-sm text-gray-700 mt-4" data-testid={`${testId}-other-brand`}>
          {brand} does not make a lock for that door yet. <Link to="/shop/find-my-lock" className="text-red-600 font-semibold">See what does fit</Link>.
        </p>
      )}
      {done && !pick && entry === 'all' && !noBrandMatch && (
        <div className="mt-2 rounded-xl border border-gray-200 bg-white p-4" data-testid={`${testId}-photos-instead`}>
          {recAll.note && <p className="text-sm text-gray-700 mb-2">{recAll.note}</p>}
          {/* The one place the shop asks for photos before a sale: a locksmith has to pick this lock (Hugh, 17 Sep 2026). */}
          <p className="text-sm text-gray-900 font-semibold">This door needs a locksmith's eye — <Link to={photosHref} className="text-red-600">send three photos of the door</Link>. We reply with the lock and the fitted price.</p>
          <button type="button" onClick={reset} className="mt-3 text-sm text-gray-700 inline-flex items-center gap-1 hover:text-gray-900"><RotateCcw size={14} /> Start again</button>
        </div>
      )}
    </div>
  );
}
